"use client"
import React, { useState, useEffect, useRef } from "react";
import useApi from "@/components/api/useApi";
import { Container, Row, Col, Media, Modal, Tab, Nav } from "react-bootstrap";
import EventCalendarLink from "@/components/shared/EventCalendarLink";
import { useRouter } from "next/navigation";

import { useMediaQuery } from "react-responsive";
import { useConfig } from "@/lib/providers/ConfigProvider";
import CollapsedHeader from "@/components/shared/CollapsedHeader";
import GoToLink from "@/components/shared/GoToLink";
import MapViewHero from "@/components/maps/MapViewHero";
import EventParticipantGroupParticipantList from "@/components/events/EventParticipantGroupParticipantList";
import EventParticipantGroupVendorList from "@/components/events/EventParticipantGroupVendorList";
import EventCalendarEntry from "@/components/events/EventCalendarEntry";
import MediaSubmissionModal from "@/components/media/MediaSubmissionModal";
import MediaItemGalleryView from "@/components/shared/MediaItemGalleryView";
import EventMapAndPinsv2 from "@/components/events/EventMapAndPinsv2";
import MapRouteDetailView from "@/components/maps/MapRouteDetailView";

import "@/components/events/EventView.css";
import EventKioskView from "@/components/events/EventKioskView";
import { useTenant } from "@/lib/providers/TenantProvider";

var moment = require("moment");

const EventView = ({
  name,
  communityName,
  id,
  memberResourceName,
  utmParams,
  programCheckin,
  hash
}) => {
  const { webUrl } = useConfig();
  const router = useRouter();
  const PARTICIPANT_GROUPS = [
    "BUSINESS",
    "VENDOR",
    "SPONSOR",
    "PERFORMER",
    "VOLUNTEER",
    "VENUE"
  ];

  const isSm = useMediaQuery({ maxWidth: 767.98 });

  const buttonRef = useRef();

  const { profile, community } = useTenant();
  const api = useApi();
  const [loaded, setLoaded] = useState(false);
  const [event, setEvent] = useState(null);
  const [eventMap, setEventMap] = useState(null);
  const [points, setPoints] = useState(null);
  const [participantPoints, setParticipantPoints] = useState(null);
  const [amenityPoints, setAmenityPoints] = useState(null);
  const [authentication, setAuthentication] = useState(null);
  const [fourOFour, setFourOFour] = useState(false);
  const [submitToGallery, setSubmitToGallery] = useState(false);
  const [referenceCommunity, setReferenceCommunity] = useState(null);
  const [mapRoutes, setMapRoutes] = useState(null);

  const [galleryItems, setGalleryItems] = useState(null);

  const [
    managedEventParticipantGroups,
    setManagedEventParticipantGroups
  ] = useState(null);

  const [allParticipants, setAllParticipants] = useState([]);
  const [flattenedParticipants, setFlattenedParticipants] = useState([]);
  const [eventSchedule, setEventSchedule] = useState(null);

  const [fullscreenToggle, setFullscreenToggle] = useState(false);

  const [selectedTab, setSelectedTab] = useState("home");
  const [tabOptions, setTabOptions] = useState(null);

  useEffect(() => {
    var storedAuth = api.getAuthentication();
    if (storedAuth != null) {
      var auth = JSON.parse(storedAuth);
      setAuthentication(auth);
    }
  }, []);

  useEffect(() => {
    if (communityName) {
      api.getProfileCommunity(communityName).then(community => {
        setReferenceCommunity(community);
      });
    }
  }, [communityName]);

  useEffect(() => {
    if (name && id) {
      api
        .getEvent(name, id, utmParams)
        .then(evt => {
          setEvent(evt);
          setLoaded(true);
          if (evt.managedEventReferenceId) {
            api
              .getManagedEventParticipantGroups(
                name,
                evt.managedEventReferenceId
              )
              .then(mepg => {
                setManagedEventParticipantGroups(mepg);
              });
            api
              .listManagedEventParticipants(name, evt.managedEventReferenceId)
              .then(allParticipants => {
                setAllParticipants(allParticipants);
                setFlattenedParticipants(Object.values(allParticipants).flat());
              });
            api
              .listManagedEventSchedule(name, evt.managedEventReferenceId)
              .then(eventSchedule => {
                setEventSchedule(eventSchedule);
              });
          } else {
            setManagedEventParticipantGroups({});
            setEventSchedule([]);
          }

          if (evt.enableEventMap) {
            api
              .getEventMap(name, id)
              .then(theEventMap => {
                setEventMap(theEventMap);
                if (theEventMap.hasRoutes) {
                  api.listExistingEventMapRoutes(name, id).then(theRoutes => {
                    setMapRoutes(theRoutes);
                  });
                } else {
                  setMapRoutes([]);
                }

                var thePoints = [];
                api
                  .getEventMapPoints(name, evt.id)
                  .then(eventMapPoints => {
                    thePoints = thePoints.concat(eventMapPoints);
                    setPoints(thePoints);
                    if (evt.managedEventReferenceId) {
                      api
                        .getEventMapParticipants(name, evt.id)
                        .then(eventMapParticipants => {
                          setParticipantPoints(eventMapParticipants);
                        });
                    } else {
                      setParticipantPoints([]);
                    }
                  })
                  .catch(error => {
                    // console.log(error);
                    setPoints(thePoints);
                    if (evt.managedEventReferenceId) {
                      api
                        .getEventMapParticipants(name, evt.id)
                        .then(eventMapParticipants => {
                          setParticipantPoints(eventMapParticipants);
                        });
                    } else {
                      setParticipantPoints([]);
                    }
                  });

                api
                  .getEventMapAmenities(name, evt.id)
                  .then(amenityPoints => {
                    setAmenityPoints(amenityPoints);
                  })
                  .catch(error => {
                    // console.log(error);
                  });
              })
              .catch(error => {
                setFourOFour(true);
                setLoaded(true);
              });
          } else {
            setMapRoutes([]);
          }

          if (evt.enableGallery) {
            api.getEventGalleryItems(name, id).then(gi => {
              setGalleryItems(gi);
            });
          }
        })
        .catch(error => {
          setFourOFour(true);
          setLoaded(true);
        });
    }
  }, [name, id]);

  useEffect(() => {
    if (
      event &&
      managedEventParticipantGroups != null &&
      eventSchedule != null &&
      mapRoutes != null
    ) {
      let theTabOptions = [];
      if (event && event.enableEventMap) {
        theTabOptions = theTabOptions.concat([{ label: "Map", value: "home" }]);
      }
      if (event && event.enableGallery) {
        theTabOptions = theTabOptions.concat([
          { label: "Gallery", value: "gallery" }
        ]);
      }
      if (event && managedEventParticipantGroups) {
        [
          "business",
          "vendor",
          "sponsor",
          "performer",
          "venue",
          "market"
        ].forEach(participantGroup => {
          if (
            managedEventParticipantGroups.hasOwnProperty(
              participantGroup.toUpperCase()
            ) &&
            managedEventParticipantGroups[participantGroup.toUpperCase()]
              .enableTab
          ) {
            var epg =
              managedEventParticipantGroups[participantGroup.toUpperCase()];
            var option = {
              label:
                epg.label ??
                participantGroup.charAt(0).toUpperCase() +
                  participantGroup.slice(1) +
                  "s",
              value: participantGroup
            };
            theTabOptions.push(option);
          }
        });
      }
      if (eventSchedule && eventSchedule.length > 0) {
        theTabOptions.push({ label: "Schedule", value: "schedule" });
      }
      if (mapRoutes && mapRoutes.length > 0) {
        mapRoutes
          .filter(route => route.enableTab)
          .forEach((route, idx) => {
            theTabOptions.push({
              label: `${route.title}`,
              value: `route-${idx}`
            });
          });
      }
      // console.log("theTabOptions", theTabOptions);
      setTabOptions(theTabOptions);
      setSelectedTab(
        theTabOptions.length > 0 ? theTabOptions[0].value : "home"
      );
    }
  }, [event, managedEventParticipantGroups, eventSchedule, mapRoutes]);

  // useEffect(() => {
  //   if (eventMap && eventSchedule && eventSchedule.length > 0 && tabOptions) {
  //     let label = "Schedule";
  //     // console.log("eventSchedule eventMap", eventSchedule, eventMap);
  //     if (
  //       eventMap &&
  //       eventMap.participantLayers?.find(pl => pl.value === "EVENT_SCHEDULE")
  //         ?.label
  //     ) {
  //       label = eventMap.participantLayers.find(
  //         pl => pl.value === "EVENT_SCHEDULE"
  //       ).label;
  //     }
  //     if (label !== "Schedule") {
  //       setTabOptions(
  //         tabOptions.map(to =>
  //           to.value === "schedule" ? { ...to, label: label } : to
  //         )
  //       );
  //     }
  //   }
  // }, [eventMap, eventSchedule]);

  useEffect(() => {
    // console.log(hash,tabOptions);
    if (hash && tabOptions && tabOptions.map(to => to.value).includes(hash)) {
      setSelectedTab(hash);
    } else {
      window.location.hash = "home";
    }
  }, [hash, tabOptions]);

  const onSubmitToGallery = (payload, token, callback) => {
    api
      .submitEventGalleryItem(name, id, payload, token)
      .then(mediaItem => {
        callback(mediaItem);
      })
      .catch(error => {});
  };

  const ContributeToGalleryButton = ({ sm }) => {
    return (
      <>
        {isSm ? (
          <div className="floating-right-buttons">
            <button
              type="button"
              id="create-content"
              className={`new-content-button ${
                submitToGallery ? "hidden" : ""
              }`}
              onClick={() => setSubmitToGallery(true)}
            >
              <i className="material-icons">add</i>
            </button>
          </div>
        ) : (
          <div className="floating-right-buttons">
            <button
              type="button"
              id="create-content"
              className={`new-content-button with-text ${
                submitToGallery ? "hidden" : ""
              }`}
              onClick={() => setSubmitToGallery(true)}
            >
              <i className="material-icons">add</i>
              <span>Add to Gallery</span>
            </button>
          </div>
        )}
      </>
    );
  };

  const onPointSubmitted = () => {
    setSubmitToGallery(false);
    window.flash_messages.alertModal({
      heading: "Pending Approval",
      textContent: (
        <div className="pending-approval-msg">
          <span className="material-icons green">check_circle</span>
          <p>
            Your contribution as been sent to this community’s admin for review.
            If approved, your contribution will appear publicly.
          </p>
        </div>
      )
    });
  };

  const NavigationTabs = () => (
    <>
      {tabOptions && tabOptions.length > 0 && (
        <div className="container">
          <Nav
            className="nav-tabs classic"
            activeKey={selectedTab}
            id="page-nav"
          >
            {tabOptions.map(to => (
              <Nav.Item>
                <Nav.Link
                  eventKey={to.value}
                  onClick={() => (window.location.hash = to.value)}
                >
                  {to.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>
      )}
    </>
  );

  const CollapsedHeaderCTA = () => (
    <>
      {tabOptions && tabOptions.length > 1 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "flexStart"
          }}
        >
          {tabOptions
            .filter(to => to.value !== selectedTab)
            .map(to => (
              <button
                style={{ fontWeight: 700, fontSize: "0.875rem" }}
                className="a"
                onClick={() => (window.location.hash = to.value)}
              >
                {to.label}
              </button>
            ))}
        </div>
      ) : (
        <>
          {event.ctas && event.ctas.length > 0 ? (
            event.ctas.map((cta, idx) => (
              <GoToLink
                classProps="a with-icon"
                ownerId={event.owner.id}
                contentId={event.id}
                type="EVENT"
                url={cta.url}
                toRender={<>{cta.label}</>}
              />
            ))
          ) : (
            <EventCalendarLink event={event} sm={isSm} />
          )}
        </>
      )}
    </>
  );

  useEffect(() => {
    if (fourOFour) router.replace("not-found");
  }, [fourOFour, router]);

  if (!loaded) {
    return <></>;
  }
  if (fourOFour) {
    return null;
  }

  const isKiosk = isSm && event && event.enableKiosk;

  if (isKiosk) {
    return (
      <EventKioskView
        name={name}
        profile={profile}
        event={event}
        eventMap={eventMap}
        managedEventParticipantGroups={managedEventParticipantGroups}
        allParticipants={allParticipants}
        participantPoints={participantPoints}
        points={points}
        amenityPoints={amenityPoints}
        tabOptions={tabOptions}
        eventSchedule={eventSchedule}
        mapRoutes={mapRoutes}
        flattenedParticipants={flattenedParticipants}
      />
    );
  }

  {
    const isContentOwner =
      authentication && authentication.profileName === event.owner.resourceName;
    // const eventUrl = "./" + event.id + "/edit";

    var startDate = moment(event.startDate.date);
    if (event.recurringOptions && event.recurringDates) {
      // find the "next" instance and set it as startDate
      var now = moment();
      var isoDate = event.recurringDates.find(d => moment(d).isAfter(now));
      if (isoDate) {
        startDate = moment(isoDate);
      }
    }
    const endDate = moment(event.endDate.date);
    const duration = endDate.diff(startDate, "days") + 1;

    const eventGroupSubmissionSection = mepg => {
      return (
        <div className="callout-active-bg-color">
          <div className="default-flex-row-layout">
            <div
              className="default-flex-column-layout"
              style={{ gap: "0.25rem" }}
            >
              <p>
                <strong>{mepg.submissionTitle}</strong>
              </p>
              <span className="small muted">{mepg.submissionSummaryText}</span>
            </div>
            <div className="flex-grow" />
            <div>
              <a
                href={`${webUrl}${name}/community/managed_events/${event.managedEventReferenceId}/groups/${mepg.type}/submission`}
                className="btn btn-primary"
                style={{ minWidth: "8rem" }}
              >
                {mepg.submissionCTA}
              </a>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="event-view">
        <MapViewHero
          coverPhotoId={event.coverPhotoAsset ? event.coverPhotoAsset.id : null}
          title={event.title}
          subtitle={event.subtitle}
          text={event.description}
          location={event.location}
          event={event}
          startDate={startDate}
          endDate={endDate}
          multiDay={event.multiDay}
        >
          {event.enableGallery && <ContributeToGalleryButton sm={isSm} />}
          {eventMap && eventMap.cta && (
            <>
              <div style={{ marginTop: "1rem" }} />
              <a
                style={{ width: "fit-content" }}
                className="btn btn-secondary with-icon"
                href={eventMap.cta.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {eventMap.cta.icon && (
                  <i className={`fas ${eventMap.cta.icon}`}></i>
                )}
                <span>{eventMap.cta.label}</span>
              </a>
            </>
          )}
        </MapViewHero>

        <div style={{ marginBottom: "2rem" }}>
          {managedEventParticipantGroups &&
            Object.keys(managedEventParticipantGroups).length > 0 &&
            Object.keys(managedEventParticipantGroups).map((key, idx) => {
              let mepg = managedEventParticipantGroups[key];
              if (
                mepg.enableSubmissions &&
                mepg.enableShowApplicationOnPublicPage &&
                (!mepg.applicationStartDate ||
                  moment(mepg.applicationStartDate).isBefore(now)) &&
                (!mepg.applicationEndDate ||
                  moment(mepg.applicationEndDate).isAfter(now))
              ) {
                return (
                  <div
                    className="container event-group-submission-section"
                    style={{ maxWidth: "960px" }}
                  >
                    {eventGroupSubmissionSection(mepg)}
                  </div>
                );
              } else {
                return <></>;
              }
            })}
        </div>

        <>{NavigationTabs()}</>

        <CollapsedHeader
          override={fullscreenToggle}
          title={event.title}
          imageId={event.owner.logoId}
          name={event.owner.displayName}
          type={event.owner.userProfileType}
          rightCTA={<>{CollapsedHeaderCTA()}</>}
        />

        <Tab.Container
          id="event-nav"
          activeKey={selectedTab}
          mountOnEnter={true}
        >
          <Tab.Content>
            <Tab.Pane eventKey="home" className="map">
              {eventMap && (
                <div className="event-map-and-pins-container">
                  <EventMapAndPinsv2
                    name={name}
                    event={event}
                    eventParticipantGroups={managedEventParticipantGroups}
                    allParticipants={allParticipants}
                    eventMap={eventMap}
                    eventSchedule={eventSchedule}
                    mapRoutes={eventMap.hasRoutes ? mapRoutes : null}
                    hasHeader={true}
                    points={points}
                    participantPoints={participantPoints}
                    amenityPoints={amenityPoints}
                    onPointSubmitted={onPointSubmitted}
                    interactable={true}
                  />
                </div>
              )}
            </Tab.Pane>
            {event.enableGallery && galleryItems && galleryItems.length > 0 && (
              <Tab.Pane eventKey="gallery">
                <div style={{ marginBottom: "2rem" }} />
                <div className="container">
                  <MediaItemGalleryView mediaItemList={galleryItems} />
                </div>
              </Tab.Pane>
            )}
            {managedEventParticipantGroups && (
              <>
                {[
                  "business",
                  "vendor",
                  "sponsor",
                  "performer",
                  "venue",
                  "market"
                ].map((participantGroup, index) => {
                  var eventParticipantGroup = managedEventParticipantGroups.hasOwnProperty(
                    participantGroup.toUpperCase()
                  )
                    ? managedEventParticipantGroups[
                        participantGroup.toUpperCase()
                      ]
                    : null;
                  if (
                    eventParticipantGroup &&
                    eventParticipantGroup.enableTab
                  ) {
                    let pointsKey = participantPoints
                      ? Object.keys(participantPoints).find(k =>
                          k.endsWith(participantGroup.toUpperCase())
                        )
                      : null;
                    let points =
                      participantGroup !== "market" &&
                      participantPoints &&
                      pointsKey
                        ? participantPoints[pointsKey]
                        : [];
                    return (
                      <Tab.Pane eventKey={participantGroup}>
                        {participantGroup === "market" ? (
                          <EventParticipantGroupVendorList
                            name={name}
                            event={event}
                            eventParticipantGroup={eventParticipantGroup}
                            participants={
                              allParticipants.hasOwnProperty(
                                eventParticipantGroup.type
                              )
                                ? allParticipants[eventParticipantGroup.type]
                                : []
                            }
                            buttonRef={buttonRef}
                          />
                        ) : (
                          <EventParticipantGroupParticipantList
                            name={name}
                            event={event}
                            eventParticipantGroup={eventParticipantGroup}
                            points={points}
                          />
                        )}
                        <div
                          ref={buttonRef}
                          id="floating-buttons"
                          className={"floating-bottom-buttons"}
                        ></div>
                      </Tab.Pane>
                    );
                  } else {
                    return <></>;
                  }
                })}
              </>
            )}
            {eventSchedule && eventSchedule.length > 0 && (
              <Tab.Pane
                eventKey="schedule"
                style={{ paddingBottom: isSm ? "40vh" : "80vh" }}
              >
                <div style={{ marginBottom: "2rem" }} />
                <div className="container">
                  <div className="event-calendar">
                    {eventSchedule.map((subEvt, idx) => {
                      var participants =
                        subEvt.participants &&
                        flattenedParticipants &&
                        flattenedParticipants.filter(
                          p =>
                            subEvt.participants.findIndex(
                              pa => pa.id === p.id
                            ) > -1
                        );
                      return (
                        <EventCalendarEntry
                          key={`event-calendar-entry-${subEvt.id}`}
                          queryDate={startDate}
                          event={subEvt}
                          community={null}
                          external={false}
                          coordinates={null}
                          showMap={false}
                          small={isSm}
                          onClick={null}
                          onShowEventsAtLocation={null}
                          participants={participants}
                        />
                      );
                    })}
                  </div>
                </div>
              </Tab.Pane>
            )}
            {mapRoutes && mapRoutes.length > 0 && (
              <>
                {mapRoutes
                  .filter(route => route.enableTab)
                  .map((route, idx) => (
                    <Tab.Pane eventKey={`route-${idx}`}>
                      <MapRouteDetailView
                        key={`map-route-detail-view-${idx}`}
                        route={route}
                        map={eventMap}
                      />
                    </Tab.Pane>
                  ))}
              </>
            )}
          </Tab.Content>
        </Tab.Container>

        {submitToGallery && (
          <MediaSubmissionModal
            name={name}
            title="Add to Gallery"
            multiple={true}
            onSubmit={(payload, callback, token) => {
              onSubmitToGallery(payload, token, success => {
                if (success) {
                  setSubmitToGallery(false);
                  callback(success);
                }
              });
            }}
            onHide={() => setSubmitToGallery(false)}
          />
        )}
      </div>
    );
  }
};
export default EventView;
